import { transient, autoinject, bindable, computedFrom, observable } from 'aurelia-framework';
import { QueryFilter, EntityMetaDataModel, DataType } from '../../../models/queries/queries.models';
import { QueryFiltersExecutor } from '../converters/queries/query-filter';
import { QueriesClientService } from '../../../services/queries/queries-client.services';
import { InitFilterValueConverter } from '../converters/queries/init-filter';
import { Reflection } from '../../../utilities/reflection';
import * as Enumerable from "linq";
import { I18N } from 'aurelia-i18n';
import { UserClientService } from '../../../services/authentication/user-client.service';
import { Guid } from '../../../utilities/guid';

@autoinject()
@transient()
export class OdataGridListView {

    private _promisesCount: number = 0;
    private _entityModel: EntityMetaDataModel;
    private _previouslySelectedItem: any = null;

    @bindable()
    title: string;

    @bindable()
    showCloseButton: boolean = true;

    @bindable()
    uri: string;

    @bindable()
    filterProperties: string[] = [];

    @bindable()
    initialFilters: QueryFilter[] = [];

    @bindable()
    fallbackText: string;

    @bindable()
    canAdd: boolean = true;

    @bindable()
    identifier: string = Guid.generateGuid();

    public selectedItem: any;
    public items: any[] = [];

    //used for searching
    @observable()
    public searchQuery: string;

    public isSearching: boolean = false;

    //used for paging
    public pageNumber: number = 0;
    public itemsPerPage: number = 10;
    public availableItems: number = 0;

    constructor(private element: Element,
        private initFilterConverter: InitFilterValueConverter,
        private queriesClientService: QueriesClientService,
        private userClientService: UserClientService,
        private i18N: I18N) {
        this.fallbackText = this.i18N.tr('generic-fallback-text')
    }

    searchQueryChanged() {
        this.loadItems();
    }

    uriChanged() {
        this.queriesClientService.getEntityModels().then(entityModels => {
            this._entityModel = entityModels.filter(entityModel => entityModel.uri == this.uri)[0];
        });
    }

    public changeSearchFieldVisibility() {
        this.isSearching = !this.isSearching;
        if (!this.isSearching) {
            this.searchQuery = null;
        }
    }

    @computedFrom(Reflection.getPropertyPath<OdataGridListView>(x => x.pageNumber))
    public get hasPrevious(): boolean {
        return this.pageNumber > 0;
    }

    @computedFrom(Reflection.getPropertyPath<OdataGridListView>(x => x.pageNumber), Reflection.getPropertyPath<OdataGridListView>(x => x.numberOfPages))
    public get hasNext(): boolean {
        return this.pageNumber < this.numberOfPages - 1;
    }

    @computedFrom(Reflection.getPropertyPath<OdataGridListView>(x => x.availableItems), Reflection.getPropertyPath<OdataGridListView>(x => x.itemsPerPage))
    public get numberOfPages(): number {
        const pages = Math.ceil(this.availableItems / this.itemsPerPage)
        //used so that when there are no search results 1/1 is displayed instead of 1/0
        return pages == 0 ? 1 : pages
    }

    public next() {
        this.pageNumber = this.pageNumber + 1;
        this.loadItems();
    }

    public previous() {
        this.pageNumber = this.pageNumber - 1;
        this.loadItems();
    }

    public selectItem(item: any, skipEvent: boolean = false) {
        this.selectedItem = item;

        if (!skipEvent) {
            const eventClick = new CustomEvent('item-selected', { detail: { event: event, item: item }, bubbles: true });
            this.element.dispatchEvent(eventClick);
        }
    }

    public newItem() {
        const eventClick = new CustomEvent('new-item', { detail: { event: event }, bubbles: true });
        this.element.dispatchEvent(eventClick);
    }

    public closeList() {
        const closeEvent = new CustomEvent('list-closed', { detail: { event: event }, bubbles: true });
        this.element.dispatchEvent(closeEvent);
    }

    public loadItems() {
        return new Promise<void>((resolve) => {
            let filters = this.initialFilters;

            if (this.searchQuery) {
                filters = filters.concat(this.getSearchFilters())
            }
            
            this._promisesCount++;
            const localPromiseCount = this._promisesCount;

            const queryFiltersExecutor = new QueryFiltersExecutor(this.initFilterConverter, filters, this.pageNumber, this.itemsPerPage, [], this.userClientService.currentUser);
            queryFiltersExecutor.getUri(false, true, this.uri, this.itemsPerPage).then(response => {
                const dataPromise = this.queriesClientService.getData("odata/" + response, this.identifier);

                if (this.selectedItem) {
                    this._previouslySelectedItem = this.selectedItem;
                }

                dataPromise.then(response => {
                    if (localPromiseCount != this._promisesCount) return;

                    this.items = response.value;
                    if (this._previouslySelectedItem) {
                        const newItemToSelect = Enumerable.from(this.items).firstOrDefault(x => x.id == this._previouslySelectedItem.id);
                        if (newItemToSelect) {
                            this.selectedItem = newItemToSelect;
                            this._previouslySelectedItem = null;
                        }
                    }
                    this.availableItems = response["@odata.count"];
                    resolve();
                });
            });
        });
    }

    private getSearchFilters() {
        const filterProperties = this.filterProperties ? this._entityModel.filterProperties.filter(z => this.filterProperties.indexOf(z.name) > -1) : [];
        const fp = filterProperties.map(fp => {
            const defaultTransaltedSymbol = this.getDefaultTransaltedSymbol(fp.type);
            const query = QueryFilter.getQuery(fp.name, this.searchQuery, defaultTransaltedSymbol.symbol, fp.type, false, false, null, false, defaultTransaltedSymbol.requiresInterpolation);
            query.groupGuid = 'search_filters';
            return query;
        });

        fp[0].isAnd = true;
        return fp;
    }

    private getDefaultTransaltedSymbol(type: DataType): { symbol: string, requiresInterpolation: boolean } {
        switch (type) {
            case DataType.Default:
                return {
                    symbol: "contains({0}, '{1}')",
                    requiresInterpolation: true
                };
            default:
                return {
                    symbol: "eq",
                    requiresInterpolation: false
                };
        }
    }

    attached() {
        this.loadItems();
    }
}
